import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import moment from 'moment';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ConfirmModal from './components/ConfirmModal';
import { API } from './api/api';

// Session-scoped cache for project → CRM account lookups (avoids re-fetching on every timelog open)
const projectAccountCache = new Map(); // projectId → { accountId, accountName }

// Helper functions for time formatting
const formatDecimalHoursToHHMM = (decimalHours) => {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const parseHHMMToDecimalHours = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours + (minutes / 60);
};

// Parse flexible time input - supports both decimal (0,1) and HH:MM (1:30) formats
const parseFlexibleTimeToDecimalHours = (timeString) => {
  if (!timeString || !timeString.trim()) return 0;
  
  const trimmed = timeString.trim();
  
  // Check if it's decimal format (contains comma)
  if (trimmed.includes(',')) {
    const decimalValue = parseFloat(trimmed.replace(',', '.'));
    if (isNaN(decimalValue)) throw new Error('Invalid decimal format');
    return decimalValue;
  }
  
  // Check if it's HH:MM format (contains colon)
  if (trimmed.includes(':')) {
    return parseHHMMToDecimalHours(trimmed);
  }
  
  // If it's just a number, treat as decimal hours
  const decimalValue = parseFloat(trimmed);
  if (isNaN(decimalValue)) throw new Error('Invalid number format');
  return decimalValue;
};

// Isolated timer display — ticks every second without re-rendering the parent form
function TimerDisplay({ startedAt, clockOffset = 0 }) {
  const calcElapsed = useCallback(() =>
    startedAt ? Math.max(0, Math.floor((Date.now() + clockOffset - startedAt) / 1000)) : 0,
    [startedAt, clockOffset]
  );
  const [elapsed, setElapsed] = useState(calcElapsed);
  useEffect(() => {
    if (!startedAt) { setElapsed(0); return; }
    setElapsed(calcElapsed());
    const interval = setInterval(() => setElapsed(calcElapsed()), 1000);
    return () => clearInterval(interval);
  }, [startedAt, calcElapsed]);
  const h = Math.floor(elapsed / 3600);
  const m = Math.floor((elapsed % 3600) / 60);
  const s = elapsed % 60;
  return (
    <span className="text-lg font-mono text-gray-700">
      {String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}
    </span>
  );
}

// Extract abbreviation from worktype name (e.g., "(IT) Diverse IT" -> "(IT)")
const extractAbbreviation = (workTypeName) => {
  const match = workTypeName.match(/^\(([^)]+)\)/);
  return match ? `(${match[1]})` : workTypeName;
};

// Ferieafrunding - afrund til nærmeste halve eller hele arbejdsdag
const applyVacationRounding = (hours, timecodeName) => {
  // Check if timecode contains "FER" (case insensitive)
  if (timecodeName && timecodeName.toUpperCase().includes('FER')) {
    // Round to either 3.75 (half day) or 7.5 (full day)
    // Midpoint at 5.625 hours = (3.75 + 7.5) / 2
    return hours <= 5.625 ? 3.75 : 7.5;
  }
  return hours; // No rounding for non-vacation codes
};

export default function EventFormModal({ isOpen, onClose, onSave, start, end, projects, initialData, onDelete, selectedSlotDate, currentView, modalPosition, onTimeChange, setModalPosition, getModalPositionStyle, onNavigate, currentDate, billableAutoFill, standalone = false, enableLeave = false, enablePeople = false, directProjectMode = false, filterProjectsByAssignment = false, preloadedAssignedProjects = null, isAdmin = false, onHasChangesChange, onRefreshProjects }) {
  const [formType, setFormType] = useState('projects'); // 'projects' | 'people' | 'workday'

  // People leave form state
  const [leaveStartDate, setLeaveStartDate] = useState('');
  const [leaveEndDate, setLeaveEndDate] = useState('');
  const [leaveType, setLeaveType] = useState('');
  const [leaveNotes, setLeaveNotes] = useState('');
  const [leaveErrors, setLeaveErrors] = useState({});
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaveSubmitError, setLeaveSubmitError] = useState('');
  const [isSubmittingLeave, setIsSubmittingLeave] = useState(false);
  const [leaveBalance, setLeaveBalance] = useState(null); // { balance, booked } or null

  useEffect(() => {
    if (formType === 'people' && leaveTypes.length === 0) {
      API.fetchLeaveTypes()
        .then(data => {
          if (data?.success && Array.isArray(data.data)) {
            setLeaveTypes(data.data);
          }
        })
        .catch(() => {});
    }
  }, [formType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (formType !== 'people') return;
    API.fetchLeaveBalance()
      .then(data => setLeaveBalance(data?.success ? { balance: data.balance, booked: data.booked } : null))
      .catch(() => setLeaveBalance(null));
  }, [formType]);

  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [hours, setHours] = useState('');
  const [projectId, setProjectId] = useState('');
  const [timecodeId, setTimecodeId] = useState('');
  const [title, setTitle] = useState('');
  const [billingtype, setBillingType] = useState('Billable');
  const [sendToHR, setSendToHR] = useState(false);
  const [projectTimecodes, setProjectTimecodes] = useState([]);
  const [endTime, setEndTime] = useState('');
  const [lastChangedField, setLastChangedField] = useState(null);
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const prevInitialDataRef = useRef(undefined);
  // Tracks which logId we've fully initialized the form for. Prevents the initialData
  // useEffect from re-wiping all fields when editingEvent's reference changes due to
  // handleModalTimeChange (which spreads new start/end on every time-field keystroke).
  const initializedLogIdRef = useRef(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [isRefreshingForm, setIsRefreshingForm] = useState(false);
  const [vacationRoundingMessage, setVacationRoundingMessage] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountProjects, setAccountProjects] = useState([]);
  const [defaultProjectId, setDefaultProjectId] = useState('');
  const [isLoadingAccountProjects, setIsLoadingAccountProjects] = useState(false);
  const [nonBemandingTasks, setNonBemandingTasks] = useState([]);
  const [nonBemandingTaskId, setNonBemandingTaskId] = useState('');

  // Assigned projects (filtered by user task ownership)
  const [assignedProjects, setAssignedProjects] = useState(null); // null = loading/not fetched

  const assignedProjectIds = useMemo(
    () => assignedProjects ? new Set(assignedProjects.map(p => String(p.id))) : null,
    [assignedProjects]
  );

  const assignedAccountOptions = useMemo(() => {
    if (!assignedProjects) return null;
    const seen = new Set();
    // Include both normal accounts and suspect accounts (using fallback account).
    // Suspect projects are served under their last-known account so the dropdown
    // appears unaffected when Zoho transiently drops the CRM link.
    return assignedProjects
      .filter(p => {
        const id = p.accountId || p.fallbackAccountId;
        return id && !seen.has(id) && seen.add(id);
      })
      .map(p => ({ value: p.accountId || p.fallbackAccountId, label: p.accountName || p.fallbackAccountName }))
      .sort((a, b) => a.label.localeCompare(b.label, 'da'));
  }, [assignedProjects]);


  // Project timer state
  const [timerStartedAt, setTimerStartedAt] = useState(null);
  const [timerClockOffset, setTimerClockOffset] = useState(0);
  const [timerStoppedInTab, setTimerStoppedInTab] = useState(null); // 'projects' | 'workday'
  const [showTimerReminder, setShowTimerReminder] = useState(false);
  const [showMarkDoneModal, setShowMarkDoneModal] = useState(false);
  const [markDoneAfterSave, setMarkDoneAfterSave] = useState(false);

  // Workday timer state
  const [workdayTimerStartedAt, setWorkdayTimerStartedAt] = useState(null);
  const [workdayTimerClockOffset, setWorkdayTimerClockOffset] = useState(0);

  // Workday form state
  const [workdayDate, setWorkdayDate] = useState('');
  const [workdayStartTime, setWorkdayStartTime] = useState('');
  const [workdayEndTime, setWorkdayEndTime] = useState('');
  const [workdayNotes, setWorkdayNotes] = useState('');
  const [workdayErrors, setWorkdayErrors] = useState({});
  const [workdaySubmitError, setWorkdaySubmitError] = useState('');
  const [isSubmittingWorkday, setIsSubmittingWorkday] = useState(false);

  // State for tracking screen size for responsive modal positioning
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Prevent body scroll when modal is open on mobile
  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.classList.add('modal-open');
      return () => {
        document.body.classList.remove('modal-open');
      };
    }
  }, [isOpen, isMobile]);

  // Seed local state from preloaded data (App.js fetches on load so dropdown is ready before first open)
  useEffect(() => {
    if (preloadedAssignedProjects !== null) {
      setAssignedProjects(preloadedAssignedProjects);
    }
  }, [preloadedAssignedProjects]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch assigned projects when modal opens — fallback if preload hasn't arrived yet
  useEffect(() => {
    if (!isOpen || !filterProjectsByAssignment) return;
    if (assignedProjects !== null) return; // already fetched or preloaded
    API.fetchAssignedProjects()
      .then(res => setAssignedProjects(res.success ? res.data : []))
      .catch(() => setAssignedProjects(null));
  }, [isOpen, filterProjectsByAssignment]); // eslint-disable-line react-hooks/exhaustive-deps


  // Fetch project timer state when modal opens
  useEffect(() => {
    if (!isOpen) return;
    API.fetchTimer()
      .then(data => {
        if (data?.success && data.running && data.startedAt) {
          setTimerStartedAt(data.startedAt);
          setTimerClockOffset(data.serverNow ? data.serverNow - Date.now() : 0);
        } else {
          setTimerStartedAt(null);
        }
      })
      .catch(() => {});
  }, [isOpen]);

  // Fetch workday timer state when modal opens
  useEffect(() => {
    if (!isOpen) return;
    API.fetchWorkdayTimer()
      .then(data => {
        if (data?.success && data.running && data.startedAt) {
          setWorkdayTimerStartedAt(data.startedAt);
          setWorkdayTimerClockOffset(data.serverNow ? data.serverNow - Date.now() : 0);
        } else {
          setWorkdayTimerStartedAt(null);
        }
      })
      .catch(() => {});
  }, [isOpen]);

  // Reset the init-once guard when the modal closes so reopening the same event re-initializes
  useEffect(() => {
    if (!isOpen) initializedLogIdRef.current = null;
  }, [isOpen]);

  const handleStartTimer = () => {
    API.startTimer()
      .then(data => {
        if (data?.startedAt) {
          setTimerStartedAt(data.startedAt);
          setTimerClockOffset(data.serverNow ? data.serverNow - Date.now() : 0);
          API.updateTaskStatus(projectId, nonBemandingTaskId, '253137000000019001').catch(() => {});
        }
      })
      .catch(console.error);
  };

  const handleStopTimer = () => {
    const stoppedAt = Math.max(Date.now(), timerStartedAt + 60 * 1000);
    API.stopTimer()
      .then(() => {
        const diffHours = (stoppedAt - timerStartedAt) / (1000 * 60 * 60);
        setDate(moment(timerStartedAt).format('YYYY-MM-DD'));
        setStartTime(moment(timerStartedAt).format('HH:mm'));
        setEndTime(moment(stoppedAt).format('HH:mm'));
        setHours(formatDecimalHoursToHHMM(diffHours));
        setTimerStartedAt(null);
        setHasChanges(true);
        setTimerStoppedInTab('projects');
        setShowMarkDoneModal(true);
      })
      .catch(console.error);
  };

  const handleMarkDoneConfirm = () => {
    setMarkDoneAfterSave(true);
    setShowMarkDoneModal(false);
    setShowTimerReminder(true);
  };

  const handleMarkDoneCancel = () => {
    setShowMarkDoneModal(false);
    setShowTimerReminder(true);
  };

  const handleStartWorkdayTimer = () => {
    API.startWorkdayTimer()
      .then(data => {
        if (data?.startedAt) {
          setWorkdayTimerStartedAt(data.startedAt);
          setWorkdayTimerClockOffset(data.serverNow ? data.serverNow - Date.now() : 0);
        }
      })
      .catch(console.error);
  };

  
  const submitWorkdayWithValues = (date, startTime, endTime, notes) => {
    const errors = {};
    if (!date) errors.workdayDate = 'Dato er påkrævet';
    if (!startTime) errors.workdayStartTime = 'Starttid er påkrævet';
    if (!endTime) errors.workdayEndTime = 'Sluttid er påkrævet';
    if (startTime && endTime) {
      const startMins = parseInt(startTime.split(':')[0]) * 60 + parseInt(startTime.split(':')[1]);
      const endMins = parseInt(endTime.split(':')[0]) * 60 + parseInt(endTime.split(':')[1]);
      if (endMins - startMins < 1) errors.workdayEndTime = 'Sluttid skal være mindst 1 minut efter starttid';
    }
    if (Object.keys(errors).length > 0) {
      setWorkdayErrors(errors);
      return;
    }
    setWorkdayErrors({});
    setWorkdaySubmitError('');
    setIsSubmittingWorkday(true);
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    const diffHours = (eh + em / 60) - (sh + sm / 60);

    let effectiveEndTime = endTime;
    let effectiveHours = diffHours;
    let effectiveDescription = notes;
    if (diffHours > 6) {
      const adjustedEndMins = eh * 60 + em - 30;
      const aeh = Math.floor(adjustedEndMins / 60);
      const aem = adjustedEndMins % 60;
      effectiveEndTime = `${String(aeh).padStart(2, '0')}:${String(aem).padStart(2, '0')}`;
      effectiveHours = diffHours - 0.5;
      effectiveDescription = notes
        ? `${notes} - 30 minutter fratrukket grundet frokost`
        : '30 minutter fratrukket grundet frokost';
    }

    const apiCall = initialData?.logId
      ? API.updatePeopleTimelog({
          timeLogId: String(initialData.logId).replace('people:', ''),
          workDate: date,
          fromTime: startTime,
          toTime: effectiveEndTime,
          hours: effectiveHours,
          description: effectiveDescription,
          jobId: initialData.timecodeId || '',
          employeeEmail: initialData.employeeEmail || '',
        })
      : API.createPeopleTimelog({
          workDate: date,
          start: startTime,
          end: effectiveEndTime,
          hours: effectiveHours,
          description: effectiveDescription,
        });

    apiCall
      .then(() => {
        setWorkdayDate('');
        setWorkdayStartTime('');
        setWorkdayEndTime('');
        setWorkdayNotes('');
        setIsSubmittingWorkday(false);
        setTimerStoppedInTab(null);
        onSave({ isWorkdaySaved: true });
      })
      .catch(err => {
        const msg = err.message || '';
        setWorkdaySubmitError(
          msg.includes('overlapper')
            ? 'Du kan ikke logge arbejdstid flere gange i samme tidsrum'
            : msg || 'Serverfejl. Prøv igen.'
        );
        setIsSubmittingWorkday(false);
      });
  };

  const handleSubmitWorkday = () => {
    submitWorkdayWithValues(workdayDate, workdayStartTime, workdayEndTime, workdayNotes);
  };

  const handleStopWorkdayTimer = () => {
    const stoppedAt = Math.max(Date.now(), workdayTimerStartedAt + 60 * 1000);
    API.stopWorkdayTimer()
      .then(() => {
        const startDate = moment(workdayTimerStartedAt).format('YYYY-MM-DD');
        const endDate   = moment(stoppedAt).format('YYYY-MM-DD');
        const start     = moment(workdayTimerStartedAt).format('HH:mm');
        const end       = moment(stoppedAt).format('HH:mm');
        setWorkdayTimerStartedAt(null);

        if (startDate === endDate) {
          setWorkdayDate(startDate);
          setWorkdayStartTime(start);
          setWorkdayEndTime(end);
          submitWorkdayWithValues(startDate, start, end, workdayNotes);
          return;
        }

        // Overnight — split into two logs
        setIsSubmittingWorkday(true);
        setWorkdaySubmitError('');

        const [sh, sm] = start.split(':').map(Number);
        const [eh, em] = end.split(':').map(Number);
        const totalMins = (1440 - (sh * 60 + sm)) + (eh * 60 + em);

        let day1End   = '23:59';
        let day2End   = end;
        let day1Notes = workdayNotes;
        let day2Notes = workdayNotes;

        if (totalMins > 360) {
          const day2Mins = eh * 60 + em;
          if (day2Mins >= 30) {
            const adjMins = day2Mins - 30;
            day2End = `${String(Math.floor(adjMins / 60)).padStart(2, '0')}:${String(adjMins % 60).padStart(2, '0')}`;
            day2Notes = workdayNotes ? `${workdayNotes} - 30 minutter fratrukket grundet frokost` : '30 minutter fratrukket grundet frokost';
          } else {
            const adj = 1439 - 30;
            day1End = `${String(Math.floor(adj / 60)).padStart(2, '0')}:${String(adj % 60).padStart(2, '0')}`;
            day1Notes = workdayNotes ? `${workdayNotes} - 30 minutter fratrukket grundet frokost` : '30 minutter fratrukket grundet frokost';
          }
        }

        const [d1eh, d1em] = day1End.split(':').map(Number);
        const day1Hours = (d1eh + d1em / 60) - (sh + sm / 60);
        const [d2eh, d2em] = day2End.split(':').map(Number);
        const day2Hours = d2eh + d2em / 60;

        let log1Saved = false;
        API.createPeopleTimelog({ workDate: startDate, start, end: day1End, hours: day1Hours, description: day1Notes })
          .then(() => {
            log1Saved = true;
            return API.createPeopleTimelog({ workDate: endDate, start: '00:00', end: day2End, hours: day2Hours, description: day2Notes });
          })
          .then(() => {
            setWorkdayDate('');
            setWorkdayStartTime('');
            setWorkdayEndTime('');
            setWorkdayNotes('');
            setIsSubmittingWorkday(false);
            onSave({ isWorkdaySaved: true });
          })
          .catch(() => {
            setIsSubmittingWorkday(false);
            if (!log1Saved) {
              setWorkdaySubmitError(
                `Din tidslog fejlede. Indtast manuelt, og del den op i to, hvis den går over midnat. Tidsloggen du prøvede at oprette er: ${startDate} ${start}–23:59 / ${endDate} 00:00–${end}`
              );
            } else {
              setWorkdayDate(endDate);
              setWorkdayStartTime('00:00');
              setWorkdayEndTime(end);
              setWorkdaySubmitError('Din tidslog fejlede delvist, indtast tid efter midnat manuelt. Formen er autoudfyldt.');
            }
          });
      })
      .catch(console.error);
  };

  const readonly = !!(initialData?.isPeople && !isAdmin) || !!initialData?.isProjectArchived;

  const isCRMSync = initialData?.logId?.toString().includes('crm:');
  const isOutlookSync = isCRMSync || initialData?.timecodeName === '(SYNC) Outlook Sync';
  const canEditProject = !readonly && isOutlookSync;

  // Account search for AsyncSelect (debounced)
  const accountDebounceRef = React.useRef(null);
  const loadAccountOptions = (inputValue, callback) => {
    clearTimeout(accountDebounceRef.current);
    const delay = inputValue ? 350 : 0;
    accountDebounceRef.current = setTimeout(async () => {
      try {
        const data = await API.searchAccounts(inputValue || '');
        callback(data.success ? data.data.map(a => ({ value: a.id, label: a.name })) : []);
      } catch {
        callback([]);
      }
    }, delay);
  };

  // Fetch projects linked to selected account
  useEffect(() => {
    if (!accountId) {
      setAccountProjects([]);
      setDefaultProjectId('');
      return;
    }

    // Edit mode: just populate accountProjects so the Projekt dropdown can render
    // the selected option. NEVER clear projectId / timecodeId / nonBemandingTaskId.
    if (initialData) {
      if (filterProjectsByAssignment && assignedProjects) {
        const fromAssigned = assignedProjects.find(p => String(p.id) === String(initialData.projectId));
        if (fromAssigned) {
          setAccountProjects([{ id: fromAssigned.id, name: fromAssigned.name }]);
        } else if (initialData.projectId && initialData.projectName) {
          // Fallback for archived projects no longer in assignedProjects
          setAccountProjects([{ id: initialData.projectId, name: initialData.projectName }]);
        }
      } else if (directProjectMode) {
        setAccountProjects(projects);
      } else {
        // Standard CRM portal: fetch account projects so the dropdown has options,
        // but DON'T clear projectId/timecodeId.
        setIsLoadingAccountProjects(true);
        API.fetchAccountProjects(accountId)
          .then(data => {
            if (data.success) setAccountProjects(data.data || []);
            else setAccountProjects([]);
          })
          .catch(() => setAccountProjects([]))
          .finally(() => setIsLoadingAccountProjects(false));
      }
      return;
    }

    // New-timelog mode: user picked/changed Kunde — clear stale selections and reload options.
    if (directProjectMode) {
      setAccountProjects(projects);
      setDefaultProjectId('');
      setProjectId('');
      setTimecodeId('');
      setProjectTimecodes([]);
      return;
    }

    if (filterProjectsByAssignment && assignedProjects) {
      setProjectId('');
      setTimecodeId('');
      setProjectTimecodes([]);
      setAccountProjects(assignedProjects.filter(p =>
        p.accountId === accountId || (p.crmLinkSuspect && p.fallbackAccountId === accountId)
      ));
      setDefaultProjectId('');
      return;
    }

    // Default CRM mode (new timelog)
    setIsLoadingAccountProjects(true);
    setProjectId('');
    setTimecodeId('');
    setProjectTimecodes([]);
    API.fetchAccountProjects(accountId)
      .then(data => {
        if (data.success) {
          setAccountProjects(data.data);
          setDefaultProjectId(data.defaultProjectId || '');
        } else {
          setAccountProjects([]);
          setDefaultProjectId('');
        }
      })
      .catch(() => {
        setAccountProjects([]);
        setDefaultProjectId('');
      })
      .finally(() => setIsLoadingAccountProjects(false));
  }, [accountId, directProjectMode, filterProjectsByAssignment, assignedProjects, projects, initialData]);

  const resetForm = () => {
    setDate('');
    setStartTime('');
    setHours('');
    setProjectId('');
    setTimecodeId('');
    setTitle('');
    setBillingType('Billable');
    setProjectTimecodes([]);
    setEndTime('');
    setVacationRoundingMessage('');
    setAccountId('');
    setAccountName('');
    setAccountProjects([]);
    setDefaultProjectId('');
    setSendToHR(false);
    setTimerStoppedInTab(null);
    setShowTimerReminder(false);
    setMarkDoneAfterSave(false);
  };

  useEffect(() => {
    if (hasSubmitted && Object.keys(errors).length === 0) {
      setHasSubmitted(false);
    }
  }, [errors, hasSubmitted]);

  // Listen for window resize to update mobile state for responsive modal positioning
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update startTime/endTime -> hours
    useEffect(() => {
      if (lastChangedField === 'startTime' || lastChangedField === 'endTime') {
        if (startTime && endTime && date) {
          const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
          const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
          if (endMoment.isAfter(startMoment)) {
            const diffInHours = endMoment.diff(startMoment, 'minutes') / 60;
            setHours(formatDecimalHoursToHHMM(diffInHours));

            // Notify parent to update event in calendar
            if (onTimeChange) {
              onTimeChange({
                ...(initialData || {}),
                start: startMoment.toDate(),
                end: endMoment.toDate(),
                logId: initialData?.logId || null,
              });
            }
          }
        }
      }
    }, [startTime, endTime, date]); 

  useEffect(() => {
    if (initialData) {
      // Skip re-initialization if we've already initialized this exact event.
      // The dep array includes start/end/initialData, but handleModalTimeChange in
      // App.js spreads a new editingEvent on every time-field keystroke. Without this
      // guard, the effect would wipe accountId / projectId / etc. on every edit.
      if (initializedLogIdRef.current === initialData.logId) {
        return;
      }
      initializedLogIdRef.current = initialData.logId;

      console.log('📝 EventFormModal initialData:', {
        timecodeId: initialData.timecodeId,
        timecodeName: initialData.timecodeName,
        projectId: initialData.projectId,
        logId: initialData.logId,
        fullInitialData: initialData
      });

      // Set correct tab when editing an existing log
      if (initialData.isPeople) {
        setFormType('workday');
        setWorkdayDate(moment(initialData.start).format('YYYY-MM-DD'));
        setWorkdayStartTime(moment(initialData.start).format('HH:mm'));
        setWorkdayEndTime(moment(initialData.end).format('HH:mm'));
        setWorkdayNotes(initialData.title || '');
      } else setFormType('projects');

      setDate(moment(initialData.start).format('YYYY-MM-DD'));
      setStartTime(moment(initialData.start).format('HH:mm'));
      const durationMs = moment(initialData.end).diff(moment(initialData.start));
      const durationHours = durationMs / 3600000; // ms to hours
      setHours(formatDecimalHoursToHHMM(durationHours));
      setProjectId(initialData.projectId || '');
      setTimecodeId(initialData.timecodeId || '');
      
      console.log('📝 Set timecodeId state to:', initialData.timecodeId);
      
      setTitle(initialData.title || '');
      setBillingType(initialData.billingtype || '');
      setEndTime(moment(initialData.end).format('HH:mm'));
      setAccountId(initialData.accountId || '');
      setAccountName(initialData.accountName || '');
      setSendToHR(!!initialData.isHR);
      setNonBemandingTaskId(initialData.taskId || '');
    } else if (start && end) {
      initializedLogIdRef.current = null;
      setDate(moment(start).format('YYYY-MM-DD'));
      setStartTime(moment(start).format('HH:mm'));
      setEndTime(moment(end).format('HH:mm'));
      const durationMs = moment(end).diff(moment(start));
      const durationHours = durationMs / 3600000; // ms to hours
      setHours(formatDecimalHoursToHHMM(durationHours));
      setWorkdayDate(moment().format('YYYY-MM-DD'));
      setWorkdayStartTime('08:00');
      setWorkdayEndTime('09:00');
    }
  }, [start, end, initialData]);

  // Old timelog account lookup — completely separate from the new-timelog flow.
  // Zoho timelogs don't store accountId. Fetch it directly from the project, no filters.
  // Results cached for the page session so repeated opens don't re-hit the API.
  useEffect(() => {
    if (!isOpen || !filterProjectsByAssignment || !initialData?.projectId || initialData?.accountId) return;
    const cached = projectAccountCache.get(initialData.projectId);
    if (cached) {
      setAccountId(cached.accountId);
      setAccountName(cached.accountName);
      return;
    }
    API.fetchProjectAccount(initialData.projectId)
      .then(data => {
        if (data.success && data.accountId) {
          projectAccountCache.set(initialData.projectId, { accountId: data.accountId, accountName: data.accountName || '' });
          setAccountId(data.accountId);
          setAccountName(data.accountName || '');
        }
      })
      .catch(() => {});
  }, [isOpen, initialData?.projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;

    if (readonly) return;

    const handleArrowKeys = (event) => {
      const tag = document.activeElement.tagName;
      const isEditable = (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        document.activeElement.isContentEditable
      );
      if (isEditable) return;

      if (!readonly && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        event.preventDefault();

        const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
        if (!startMoment.isValid() || !endMoment.isValid()) return;

        const diff = endMoment.diff(startMoment, 'minutes');
        if (diff <= 0) return;

        const delta = event.key === 'ArrowUp' ? -15 : 15;

        if (event.shiftKey) {
          // Shift + Arrow: move only end time
          const newEnd = endMoment.clone().add(delta, 'minutes');
          // Check if new duration would exceed 23 hours
          const newDurationHours = newEnd.diff(startMoment, 'minutes') / 60;
          // Prevent end before start, after day end, or duration over 23 hours
          if (
            newEnd.isSameOrBefore(startMoment) ||
            newEnd.isAfter(moment(date).endOf('day').add(1, 'minute')) ||
            newDurationHours > 23
          ) {
            return;
          }
          setEndTime(newEnd.format('HH:mm'));
          setLastChangedField('endTime');

          // Notify parent
          if (onTimeChange && initialData) {
            onTimeChange({
              ...initialData,
              start: startMoment.toDate(),
              end: newEnd.toDate(),
            });
          }
          if (onTimeChange && !initialData && start && end) {
            onTimeChange({
              start: startMoment.toDate(),
              end: newEnd.toDate(),
              logId: null,
            });
          }
        } else {
          // Arrow: move both start and end
          const newStart = startMoment.clone().add(delta, 'minutes');
          const newEnd = endMoment.clone().add(delta, 'minutes');
          // Check if duration would exceed 23 hours (should remain same, but double-check)
          const durationHours = newEnd.diff(newStart, 'minutes') / 60;
          if (
            newStart.isBefore(moment(date).startOf('day')) ||
            newEnd.isAfter(moment(date).endOf('day').add(1, 'minute')) ||
            durationHours > 23
          ) {
            return;
          }
          setStartTime(newStart.format('HH:mm'));
          setEndTime(newEnd.format('HH:mm'));
          setLastChangedField('startTime');

          // Notify parent
          if (onTimeChange && initialData) {
            onTimeChange({
              ...initialData,
              start: newStart.toDate(),
              end: newEnd.toDate(),
            });
          }
          if (onTimeChange && !initialData && start && end) {
            onTimeChange({
              start: newStart.toDate(),
              end: newEnd.toDate(),
              logId: null,
            });
          }
        }
      }
      if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && (currentView === 'week' || currentView === 'work_week')) {
        event.preventDefault();

        const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
        if (!startMoment.isValid() || !endMoment.isValid()) return;

        const dayOfWeek = startMoment.day(); // 0=Sunday, 1=Monday, ..., 6=Saturday

        // Week view: block left on Monday, right on Sunday
        if (currentView === 'week') {
          if (event.key === 'ArrowLeft' && dayOfWeek === 1) return;
          if (event.key === 'ArrowRight' && dayOfWeek === 0) return;
        }
        // Work week: block left on Monday, right on Friday
        if (currentView === 'work_week') {
          if (event.key === 'ArrowLeft' && dayOfWeek === 1) return;
          if (event.key === 'ArrowRight' && dayOfWeek === 5) return;
          // Also block moving to weekend
          if (event.key === 'ArrowRight' && dayOfWeek >= 5) return;
          if (event.key === 'ArrowLeft' && dayOfWeek <= 1) return;
        }

        // Calculate new start/end
        const deltaDays = event.key === 'ArrowLeft' ? -1 : 1;
        const newStart = startMoment.clone().add(deltaDays, 'days');
        const newEnd = endMoment.clone().add(deltaDays, 'days');

        // For work_week, prevent moving to Sat/Sun
        if (currentView === 'work_week') {
          const newDay = newStart.day();
          if (newDay === 0 || newDay === 6) return;
        }


        setDate(newStart.format('YYYY-MM-DD'));
        setStartTime(newStart.format('HH:mm'));
        setEndTime(newEnd.format('HH:mm'));
        setLastChangedField('startTime');

        // For three_day view, check if we need to navigate the calendar
        let shouldUpdateModalPosition = true;
        if (currentView === 'three_day' && onNavigate && currentDate) {
          // Calculate current 3-day range shown in calendar
          const calendarStart = moment(currentDate).startOf('day');
          const calendarEnd = moment(calendarStart).add(2, 'days').endOf('day');
          
          // Check if new date is outside current 3-day range
          if (newStart.isBefore(calendarStart, 'day') || newStart.isAfter(calendarEnd, 'day')) {
            // Navigate calendar to show the new date
            onNavigate(newStart.toDate());
            // Delay modal position update with the new calculated range
            shouldUpdateModalPosition = false;
            setTimeout(() => {
              // Calculate the new 3-day range that will be shown
              const newRangeStart = newStart.startOf('day');
              const dayIndex = newStart.diff(newRangeStart, 'days'); // This will be 0
              
              // Position modal based on the new range
              let newPositionStyle = { right: '20px' };
              if (dayIndex === 2) {
                newPositionStyle = { left: '20px' };
              }
              
              setModalPosition({
                ...newPositionStyle,
                position: 'fixed',
                top: '80px',
                zIndex: 2600,
              });
            }, 50);
          }
        }

        // Update modal position so it doesn't cover the event (for views without navigation)
        if (shouldUpdateModalPosition) {
          setModalPosition(getModalPositionStyle(newStart.toDate(), currentView));
        }

        // Notify parent
        if (onTimeChange && initialData) {
          onTimeChange({
            ...initialData,
            start: newStart.toDate(),
            end: newEnd.toDate(),
          });
        }
        if (onTimeChange && !initialData && start && end) {
          onTimeChange({
            start: newStart.toDate(),
            end: newEnd.toDate(),
            logId: null,
          });
        }
      }
    };

    document.addEventListener('keydown', handleArrowKeys);
    return () => document.removeEventListener('keydown', handleArrowKeys);
  }, [isOpen, date, startTime, endTime, readonly, onTimeChange, initialData, start, end]);

  useEffect(() => {
    if (projectId) {
      API.fetchTasksByProject(projectId)
        .then((data) => {
          if (data.success) {
            setProjectTimecodes(data.data);
            if (timecodeId && !data.data.find(tc => tc.id === timecodeId)) {
              setTimecodeId('');
            }
          } else {
            setProjectTimecodes([]);
            setTimecodeId('');
          }
        })
        .catch(() => {
          console.log('❌ Error fetching tasks');
          setProjectTimecodes([]);
          setTimecodeId('');
        });
    } else {
      setProjectTimecodes([]);
      setTimecodeId('');
    }
  }, [projectId, timecodeId]);

  // Fetch tasks when project changes (only tasks with no owner or user is owner)
  useEffect(() => {
    if (projectId) {
      API.fetchNonBemandingTasks(projectId)
        .then(data => {
          if (data.success) setNonBemandingTasks(data.data);
          else setNonBemandingTasks([]);
        })
        .catch(() => setNonBemandingTasks([]));
    } else {
      setNonBemandingTasks([]);
    }
    setNonBemandingTaskId(
      initialData?.projectId === projectId ? (initialData?.taskId || '') : ''
    );
  }, [projectId]);

  // Auto-set billing type when a timecode is selected — don't reset when cleared
  useEffect(() => {
    const selected = projectTimecodes.find(tc => tc.id === timecodeId);
    if (selected) {
      setBillingType(selected.billingtype || 'Billable');
    }
  }, [timecodeId, projectTimecodes]);

  // Check for vacation rounding when hours or timecode changes
  useEffect(() => {
    if (hours && timecodeId) {
      try {
        const currentHours = parseHHMMToDecimalHours(hours);
        const selectedTimecode = projectTimecodes.find(tc => tc.id === timecodeId);
        const timecodeName = selectedTimecode?.name || '';
        
        if (timecodeName.toUpperCase().includes('FER')) {
          const roundedHours = applyVacationRounding(currentHours, timecodeName);
          if (roundedHours !== currentHours) {
            const dayType = roundedHours === 3.75 ? 'half day' : 'full day';
            setVacationRoundingMessage(`Holiday hours will be rounded to ${roundedHours} hours (${dayType})`);
          } else {
            setVacationRoundingMessage('');
          }
        } else {
          setVacationRoundingMessage('');
        }
      } catch (e) {
        setVacationRoundingMessage('');
      }
    } else {
      setVacationRoundingMessage('');
    }
  }, [hours, timecodeId, projectTimecodes]);

  // Auto-fill notes based on worktype selection
  useEffect(() => {
    const selected = projectTimecodes.find(tc => tc.id === timecodeId);
    if (selected) {
      // Skip auto-fill if this is an existing entry with the same timecode (preserve Zoho Projects notes)
      if (initialData && initialData.timecodeId === timecodeId && initialData.title && initialData.title.trim()) {
        return; // Don't overwrite original notes from Zoho Projects
      }

      if (selected.billingtype === 'Non Billable' || billableAutoFill) {
        // Set notes to worktype abbreviation for non-billable tasks
        const workTypeName = selected.name || '';
        const abbreviation = extractAbbreviation(workTypeName);
        const currentTitle = title;
        
        // Check if current title contains "|" separator
        if (currentTitle.includes(' | ')) {
          // Replace only the worktype part (before "|"), keep user text after "|"
          const userText = currentTitle.split(' | ').slice(1).join(' | ');
          setTitle(`${abbreviation} | ${userText}`);
        } else {
          // For Non-Billable, preserve user text and just add abbreviation prefix
          // Check if currentTitle is any worktype abbreviation pattern (starts with "(" and ends with ")")
          const isWorktypeAbbreviation = /^\([^)]+\)$/.test(currentTitle.trim());
          
          if (currentTitle.trim() && !isWorktypeAbbreviation) {
            setTitle(`${abbreviation} | ${currentTitle}`);
          } else {
            setTitle(abbreviation);
          }
        }
      } else {
        // For billable tasks, preserve user-entered text
        // Only clear if it's clearly auto-generated (contains specific patterns) AND empty of user content
        const isAutoGenerated = title.includes(' | ') && title.split(' | ').length === 2 && !title.split(' | ')[1].trim();
        if (isAutoGenerated || !title.trim()) {
          setTitle('');
        }
        // If user has written custom text, always preserve it
      }
    }
  }, [timecodeId, projectTimecodes, initialData, billableAutoFill]);

  useEffect(() => {
    validateForm();
  }, [date, startTime, hours, projectId, timecodeId, title]);

  // Tilføj event listener for Escape-tasten
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, hasChanges, readonly]);

  const validateForm = () => {
    const newErrors = {};

    if (!date) newErrors.date = "Vælg venligst en dato";
    if (!startTime) newErrors.startTime = "Indtast venligst en starttid";
    if (startTime && endTime) {
      const startMins = parseInt(startTime.split(':')[0]) * 60 + parseInt(startTime.split(':')[1]);
      const endMins = parseInt(endTime.split(':')[0]) * 60 + parseInt(endTime.split(':')[1]);
      if (endMins - startMins < 1) newErrors.endTime = "Sluttid skal være mindst 1 minut efter starttid";
    }
    if (!hours) newErrors.hours = "Indtast venligst timer";
    else {
      // Support both HH:MM and decimal formats
      const hhmmPattern = /^\d{1,2}:\d{2}$/;
      const decimalPattern = /^\d*[,.]?\d+$/;

      if (!hhmmPattern.test(hours) && !decimalPattern.test(hours)) {
        newErrors.hours = "Brug format som 1:30, 0,1 eller 1.5";
      } else {
        try {
          const numericHours = parseFlexibleTimeToDecimalHours(hours);
          if (numericHours <= 0) newErrors.hours = "Timer skal være større end nul";
          if (numericHours > 23) newErrors.hours = "Timer skal være under 23";
        } catch (e) {
          newErrors.hours = "Ugyldigt tidsformat";
        }
      }
    }

    if (!sendToHR) {
      if (!accountId && !initialData) newErrors.accountId = "Vælg venligst en konto";
      if (!projectId && !defaultProjectId) newErrors.projectId = "Intet standardprojekt fundet — vælg venligst et";
      if (projectId && nonBemandingTasks.length > 0 && !nonBemandingTaskId) newErrors.nonBemandingTaskId = "Vælg venligst en opgave";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    setHasSubmitted(true);
    const isValid = validateForm();
    if (!isValid) {
      console.log('Form validation failed:', errors);
      return;
    }

    const startDate = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm').toDate();
    let floatHours;
    try {
      floatHours = parseFlexibleTimeToDecimalHours(hours);
    } catch (e) {
      setErrors(prev => ({ ...prev, hours: 'Ugyldigt tidsformat' }));
      return;
    }
    if (isNaN(floatHours) || floatHours <= 0) {
      setErrors(prev => ({ ...prev, hours: 'Ugyldigt timeantal' }));
      return;
    }

    // Apply vacation rounding for FER timecodes
    const selectedTimecode = projectTimecodes.find(tc => tc.id === timecodeId);
    const timecodeName = selectedTimecode?.name || '';
    floatHours = applyVacationRounding(floatHours, timecodeName);

    const durationMs = floatHours * 3600000; // Convert hours to ms
    const endDate = new Date(startDate.getTime() + durationMs);

    const isCRMSync = initialData?.logId?.toString().includes('crm:');


    const resolvedProjectId = projectId || defaultProjectId;

    const saveData = {
      title,
      start: startDate,
      end: endDate,
      projectId: resolvedProjectId,
      timecodeId,
      billingtype,
      logId: initialData?.logId || null,
      projectName: projectOptions.find(p => p.value === resolvedProjectId)?.label || '',
      timecodeName: timecodeOptions.find(tc => tc.value === timecodeId)?.label || '',
      accountId,
      accountName,
      sendToHR,
      taskId: nonBemandingTaskId || null,
      taskName: nonBemandingTaskId
        ? (nonBemandingTasks.find(t => t.id === nonBemandingTaskId)?.name || initialData?.taskName || '')
        : '',
      markTaskDone: markDoneAfterSave,
    };

    if (isCRMSync && initialData && resolvedProjectId && timecodeId) {
      saveData.convertFromCRM = true;
      saveData.logId = initialData.logId;
    }

    onSave(saveData);
    setHasSubmitted(false);
    resetForm();
    if (!standalone) onClose();
  };


  const handleDelete = () => {
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    // Pass isPeople so caller uses correct API and strips prefix
    onDelete(initialData?.logId, initialData?.projectId, initialData?.timecodeId, initialData?.isPeople, initialData?.taskId);
    resetForm();
    onClose();
    setShowDeleteConfirmModal(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmModal(false);
  };

  const handleLeaveSubmit = () => {
    const errs = {};
    if (!leaveStartDate) errs.leaveStartDate = 'Please select a start date';
    if (!leaveEndDate) errs.leaveEndDate = 'Please select an end date';
    if (leaveStartDate && leaveEndDate && leaveEndDate < leaveStartDate) errs.leaveEndDate = 'End date must be after start date';
    if (!leaveType) errs.leaveType = 'Please select a type';
    if (leaveNotes && leaveNotes.trim().length > 30) errs.leaveNotes = 'Bemærkning må max være 30 tegn';
    setLeaveErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setLeaveSubmitError('');
    setShowLeaveConfirm(true);
  };

  const handleLeaveConfirmed = async () => {
    setShowLeaveConfirm(false);
    setIsSubmittingLeave(true);
    setLeaveSubmitError('');
    try {
      await onSave({
        isPeopleLeave: true,
        leaveStartDate,
        leaveEndDate,
        leaveType,
        leaveNotes,
      });
      setLeaveStartDate('');
      setLeaveEndDate('');
      setLeaveType('');
      setLeaveNotes('');
      setLeaveErrors({});
      if (!standalone) onClose();
    } catch (err) {
      setLeaveSubmitError(err.message || 'Serverfejl. Tjek forbindelsen og prøv igen.');
    } finally {
      setIsSubmittingLeave(false);
    }
  };

  const handleClose = () => {
    if (hasChanges && !readonly) {
      setShowConfirmModal(true);
    } else {
      resetForm();
      onClose();
      setHasSubmitted(false);
      setHasChanges(false);
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmModal(false);
    resetForm();
    onClose();
    setHasSubmitted(false);
    setHasChanges(false);
  };

  const handleCancelClose = () => {
    setShowConfirmModal(false);
  };

  // Tjek for ændringer når inputs ændres
  useEffect(() => {
    if (initialData !== prevInitialDataRef.current) {
      prevInitialDataRef.current = initialData;
      setHasChanges(false);
      return;
    }
    if (!readonly) {
      if (formType === 'workday') {
        if (!initialData) {
          setHasChanges(!!timerStoppedInTab || workdayNotes !== '');
        } else {
          setHasChanges(
            !!timerStoppedInTab ||
            workdayDate !== moment(initialData.start).format('YYYY-MM-DD') ||
            workdayStartTime !== moment(initialData.start).format('HH:mm') ||
            workdayEndTime !== moment(initialData.end).format('HH:mm') ||
            workdayNotes !== (initialData.title || '')
          );
        }
      } else {
        setHasChanges(
          !!timerStoppedInTab ||
          projectId !== (initialData?.projectId || '') ||
          timecodeId !== (initialData?.timecodeId || '') ||
          title !== (initialData?.title || '') ||
          (!!initialData && date !== moment(initialData.start).format('YYYY-MM-DD')) ||
          (!!initialData && startTime !== moment(initialData.start).format('HH:mm')) ||
          (!!initialData && endTime !== moment(initialData.end).format('HH:mm'))
        );
      }
    }
  }, [projectId, timecodeId, title, initialData, readonly, formType, workdayDate, workdayStartTime, workdayEndTime, workdayNotes, date, startTime, endTime, timerStoppedInTab]);

  useEffect(() => {
    onHasChangesChange?.(hasChanges, timerStoppedInTab);
  }, [hasChanges, timerStoppedInTab]);

  if (!isOpen) return null;

  const projectOptions = (accountId ? accountProjects : projects).map(p => ({
    value: p.id,
    label: p.name,
  }));

  const timecodeOptions = projectTimecodes.map((tc) => ({
    value: tc.id,
    label: tc.name,
  }));

  // Mobile-first positioning: center on mobile with respect to fixed header/footer
  const mobileStyles = {
    position: 'fixed',
    top: '20px', // Minimal margin from top (header will be 70px + 20px = 90px from top)
    left: '50%',
    transform: 'translateX(-50%)', // Only center horizontally
    zIndex: 2600,
    bottom: '20px', // Minimal margin from bottom (footer will be 90px + 20px = 110px from bottom)
    // Let the modal content scroll if needed
    display: 'flex',
    flexDirection: 'column',
  };

  const finalModalPosition = isMobile ? mobileStyles : modalPosition;

  return (
    <>
      {/* Backdrop overlay — only in popup mode */}
      {!standalone && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 md:bg-transparent"
          style={{ zIndex: 2500 }}
          onClick={handleClose}
        />
      )}

      <div
        className={standalone
          ? "bg-white rounded-lg w-full max-w-lg shadow-sm border border-gray-200 flex flex-col"
          : "bg-white rounded-lg w-full max-w-[88vw] md:w-[400px] h-auto max-h-full md:max-h-[90vh] shadow-xl border border-gray-300 modal-mobile flex flex-col"
        }
        style={standalone ? {} : finalModalPosition}
        onClick={(e) => e.stopPropagation()}
      >
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-3 p-3 md:p-6 pb-0 md:pb-0">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-gray-900">
                {initialData?.isPeopleLeave ? 'Fravær' : initialData ? 'Rediger' : 'Ny registrering'}
              </h2>
              {standalone && !initialData && (
                <button
                  onClick={async () => {
                    setIsRefreshingForm(true);
                    setAccountId('');
                    setAccountName('');
                    setProjectId('');
                    setTimecodeId('');
                    setNonBemandingTaskId('');
                    setAccountProjects([]);
                    setProjectTimecodes([]);
                    try {
                      const [projectsData] = await Promise.all([
                        API.refreshProjectsList(),
                        filterProjectsByAssignment ? API.refreshAssignedProjects().then(res => {
                          setAssignedProjects(res.success ? res.data : []);
                        }).catch(() => setAssignedProjects([])) : Promise.resolve(),
                      ]);
                      if (projectsData?.success && onRefreshProjects) onRefreshProjects(projectsData.data);
                    } catch {
                      // silently ignore
                    } finally {
                      setIsRefreshingForm(false);
                    }
                  }}
                  disabled={isRefreshingForm}
                  title="Opdater projekter og kunder"
                  className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  <svg className={`w-4 h-4 ${isRefreshingForm ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              )}
            </div>
            {!standalone && (
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 p-1 transition-colors"
                aria-label="Close"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            )}
          </div>

          {/* Form Type Toggle — shown when leave or people feature is enabled, hidden when editing existing log */}
          {(enableLeave || enablePeople) && !initialData && (
            <div className="flex mx-3 md:mx-6 mb-3 rounded-lg border border-gray-300 overflow-hidden">
              <button
                onClick={() => setFormType('projects')}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${formType === 'projects' ? 'bg-[#537D5D] text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                Projekt
              </button>
              {enablePeople && (
                <button
                  onClick={() => setFormType('workday')}
                  className={`flex-1 py-2 text-sm font-medium border-l border-gray-300 transition-colors ${formType === 'workday' ? 'bg-[#537D5D] text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  Arbejdstid
                </button>
              )}
              {enableLeave && (
                <button
                  onClick={() => setFormType('people')}
                  className={`flex-1 py-2 text-sm font-medium border-l border-gray-300 transition-colors ${formType === 'people' ? 'bg-[#537D5D] text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  HR
                </button>
              )}
            </div>
          )}

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-3 md:p-6 pt-0 md:pt-0">

          {/* Leave (Fravær) read-only detail view */}
          {initialData?.isPeopleLeave && (
            <div className="space-y-3 mt-2">
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-500">Type</span>
                <span className="text-base text-gray-900">{initialData.leaveTypeName || initialData.title || '—'}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-500">Dato</span>
                <span className="text-base text-gray-900">{moment(initialData.start).format('DD-MM-YYYY')}</span>
              </div>
              {initialData.employeeName && (
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-500">Medarbejder</span>
                  <span className="text-base text-gray-900">{initialData.employeeName}</span>
                </div>
              )}
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-500">Status</span>
                <span className={`text-base font-medium ${initialData.leaveStatus === 'Approved' ? 'text-green-700' : initialData.leaveStatus === 'Rejected' ? 'text-red-600' : 'text-yellow-600'}`}>
                  {initialData.leaveStatus === 'Approved' ? 'Godkendt' : initialData.leaveStatus === 'Rejected' ? 'Afvist' : 'Afventer'}
                </span>
              </div>
              {initialData.notes && (
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-500">Årsag til fravær</span>
                  <span className="text-base text-gray-900 whitespace-pre-wrap">{initialData.notes}</span>
                </div>
              )}
            </div>
          )}

          {/* Arbejdstid (People timelog) form */}
          {formType === 'workday' && (
            <div>
              {/* Workday timer — only shown for new entries */}
              {!initialData && (
                <div className="mb-4 flex flex-col items-center gap-2">
                  <button
                    onClick={workdayTimerStartedAt ? handleStopWorkdayTimer : handleStartWorkdayTimer}
                    className={`w-full py-3 rounded font-medium text-white transition-colors ${workdayTimerStartedAt ? 'bg-red-700 hover:bg-red-800' : 'bg-green-600 hover:bg-green-700'}`}
                  >
                    {workdayTimerStartedAt ? 'Stop' : 'Start'}
                  </button>
                  <TimerDisplay startedAt={workdayTimerStartedAt} clockOffset={workdayTimerClockOffset} />
                </div>
              )}

              <div className="mb-2 flex flex-col">
                <label className="text-sm md:text-base font-medium mb-1">Dato:</label>
                <input
                  type="date"
                  value={workdayDate}
                  onChange={(e) => setWorkdayDate(e.target.value)}
                  disabled={readonly}
                  className={`p-2 text-base rounded border border-gray-300 w-full touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
                />
                {workdayErrors.workdayDate && <div className="text-red-500 text-sm mt-1">{workdayErrors.workdayDate}</div>}
              </div>

              <div className="mb-2 grid grid-cols-2 gap-2">
                <div className="flex flex-col">
                  <label className="text-sm md:text-base font-medium mb-1">Start:</label>
                  <input
                    type="time"
                    value={workdayStartTime}
                    onChange={(e) => setWorkdayStartTime(e.target.value)}
                    disabled={readonly}
                    className={`p-2 text-base rounded border border-gray-300 w-full touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
                  />
                  {workdayErrors.workdayStartTime && <div className="text-red-500 text-sm mt-1">{workdayErrors.workdayStartTime}</div>}
                </div>
                <div className="flex flex-col">
                  <label className="text-sm md:text-base font-medium mb-1">Slut:</label>
                  <input
                    type="time"
                    value={workdayEndTime}
                    onChange={(e) => setWorkdayEndTime(e.target.value)}
                    disabled={readonly}
                    className={`p-2 text-base rounded border border-gray-300 w-full touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
                  />
                  {workdayErrors.workdayEndTime && <div className="text-red-500 text-sm mt-1">{workdayErrors.workdayEndTime}</div>}
                </div>
              </div>

              {workdaySubmitError && (
                <div className="mt-2 p-3 bg-red-50 border border-red-300 rounded text-red-700 text-sm">{workdaySubmitError}</div>
              )}

            </div>
          )}

          {/* People leave form */}
          {formType === 'people' && (
            <div>
              <div className="mb-2 flex flex-col">
                <label className="text-sm md:text-base font-medium mb-1">Type:</label>
                <select
                  value={leaveType}
                  onChange={(e) => setLeaveType(e.target.value)}
                  className="p-2 text-base rounded border border-gray-300 w-full touch-manipulation bg-white"
                >
                  <option value="">Select type...</option>
                  {leaveTypes.map(lt => (
                    <option key={lt.id} value={lt.id}>{lt.name}</option>
                  ))}
                </select>
                {leaveErrors.leaveType && <div className="text-red-500 text-sm mt-1">{leaveErrors.leaveType}</div>}
              </div>
              <div className="mb-2 grid grid-cols-2 gap-2">
                <div className="flex flex-col">
                  <label className="text-sm md:text-base font-medium mb-1">Fra:</label>
                  <input
                    type="date"
                    value={leaveStartDate}
                    onChange={(e) => { setLeaveStartDate(e.target.value); if (!leaveEndDate || e.target.value > leaveEndDate) setLeaveEndDate(e.target.value); }}
                    className="p-2 text-base rounded border border-gray-300 w-full touch-manipulation"
                  />
                  {leaveErrors.leaveStartDate && <div className="text-red-500 text-sm mt-1">{leaveErrors.leaveStartDate}</div>}
                </div>
                <div className="flex flex-col">
                  <label className="text-sm md:text-base font-medium mb-1">Til:</label>
                  <input
                    type="date"
                    value={leaveEndDate}
                    min={leaveStartDate || undefined}
                    onChange={(e) => setLeaveEndDate(e.target.value)}
                    className="p-2 text-base rounded border border-gray-300 w-full touch-manipulation"
                  />
                  {leaveErrors.leaveEndDate && <div className="text-red-500 text-sm mt-1">{leaveErrors.leaveEndDate}</div>}
                </div>
              </div>
              <div className="mb-2 flex flex-col">
                <label className="text-sm md:text-base font-medium mb-1">Bemærkning:</label>
                <textarea
                  value={leaveNotes}
                  onChange={(e) => setLeaveNotes(e.target.value)}
                  placeholder="Kort begrundelse for fraværet..."
                  rows={3}
                  className="p-2 text-base rounded border border-gray-300 w-full touch-manipulation resize-none"
                />
                <div className={`text-xs mt-1 text-right ${leaveNotes.trim().length > 30 ? 'text-red-500' : 'text-gray-400'}`}>
                  {leaveNotes.trim().length}/30
                </div>
                {leaveErrors.leaveNotes && <div className="text-red-500 text-sm mt-1">{leaveErrors.leaveNotes}</div>}
                {leaveBalance !== null && (
                  <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 flex flex-col gap-1">
                    {leaveBalance.balance !== null && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-black">Ferie tilgængeligt</span>
                        <span className={`text-sm font-semibold ${leaveBalance.balance < 0 ? 'text-red-500' : 'text-gray-700'}`}>
                          {leaveBalance.balance} {Math.abs(leaveBalance.balance) === 1 ? 'dag' : 'dage'}
                        </span>
                      </div>
                    )}
                    {leaveBalance.booked !== null && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-black">Ferie booket</span>
                        <span className="text-sm font-semibold text-gray-700">
                          {leaveBalance.booked} {Math.abs(leaveBalance.booked) === 1 ? 'dag' : 'dage'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {leaveSubmitError && (
                <div className="mt-2 p-3 bg-red-50 border border-red-300 rounded text-red-700 text-sm">{leaveSubmitError}</div>
              )}
            </div>
          )}

          {formType === 'projects' && !initialData?.isPeopleLeave && <>

          {initialData?.isProjectArchived && (
            <div className="mb-3 px-3 py-2 rounded bg-amber-50 border border-amber-300 text-amber-800 text-sm">
              Projektet er afsluttet - tidslog kan ikke redigeres
            </div>
          )}

          {/* Date og Hours - side by side on mobile like Start Time/End Time */}
          <div className="mb-2 grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-4">
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Dato:</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`p-2 text-base rounded border border-gray-300 w-full touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
                disabled={readonly}
              />
              {hasSubmitted && errors.date && <div className="text-red-500 text-sm mt-1">{errors.date}</div>}
            </div>
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Timer:</label>
              <input
                type="text"
                value={hours}
                onChange={(e) => {
                  // Allow numbers, colon, comma, and period for flexible input
                  let val = e.target.value.replace(/[^0-9:,.]/g, '');
                  setHours(val);
                  setLastChangedField('hours');
                }}
                onBlur={() => {
                  // Try to parse and validate the hours input using flexible parser
                  try {
                    const parsed = parseFlexibleTimeToDecimalHours(hours);
                    if (!isNaN(parsed) && parsed > 0 && parsed <= 23) {
                      // Valid: update end time
                      const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                      const endMoment = startMoment.clone().add(parsed, 'hours');
                      setEndTime(endMoment.format('HH:mm'));
                      // Notify parent
                      if (onTimeChange) {
                        onTimeChange({
                          ...(initialData || {}),
                          start: startMoment.toDate(),
                          end: endMoment.toDate(),
                          logId: initialData?.logId || null,
                        });
                      }
                    } else {
                      // Invalid: revert to current diff between start and end
                      const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                      const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
                      if (startMoment.isValid() && endMoment.isValid() && endMoment.isAfter(startMoment)) {
                        const diffInHours = endMoment.diff(startMoment, 'minutes') / 60;
                        setHours(formatDecimalHoursToHHMM(diffInHours));
                      } else {
                        setHours('');
                      }
                    }
                  } catch (e) {
                    // Invalid format: revert to current diff between start and end
                    const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                    const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
                    if (startMoment.isValid() && endMoment.isValid() && endMoment.isAfter(startMoment)) {
                      const diffInHours = endMoment.diff(startMoment, 'minutes') / 60;
                      setHours(formatDecimalHoursToHHMM(diffInHours));
                    } else {
                      setHours('');
                    }
                  }
                }}
                className={`p-2 text-base rounded border border-gray-300 w-full touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
                disabled={readonly}
                placeholder="F.eks. 1:30 eller 0,1 eller 1.5"
              />
              {hasSubmitted && errors.hours && <div className="text-red-500 text-sm mt-1">{errors.hours}</div>}
              {vacationRoundingMessage && (
                <div className="text-gray-500 text-xs mt-1 font-medium">
                  {vacationRoundingMessage}
                </div>
              )}
            </div>
          </div>

          {/* Start Time og End Time - side by side on mobile like SeriesModal */}
          <div className="mb-2 grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-4">
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Starttid:</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => {
                  setStartTime(e.target.value);
                  setLastChangedField('startTime');
                }}
                className={`p-2 text-base rounded border border-gray-300 w-full touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
                disabled={readonly}
              />
              {hasSubmitted && errors.startTime && <div className="text-red-500 text-sm mt-1">{errors.startTime}</div>}
            </div>
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Sluttid:</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => {
                  setEndTime(e.target.value);
                  setLastChangedField('endTime');
                }}
                className={`p-2 text-base rounded border border-gray-300 w-full touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
                disabled={readonly}
              />
              {hasSubmitted && errors.endTime && <div className="text-red-500 text-sm mt-1">{errors.endTime}</div>}
            </div>
          </div>

          {!sendToHR && <>
          {/* Kunde (mandatory) */}
          <div className="mb-2 flex flex-col">
            <label className="text-sm md:text-base font-medium mb-1">Kunde:</label>
            {filterProjectsByAssignment && assignedAccountOptions ? (
              <Select
                options={assignedAccountOptions}
                value={accountId ? { value: accountId, label: accountName } : null}
                onChange={(selectedOption) => {
                  if (!initialData || canEditProject) {
                    setAccountId(selectedOption?.value || '');
                    setAccountName(selectedOption?.label || '');
                    setProjectId('');
                    setNonBemandingTaskId('');
                  }
                }}
                isClearable={!initialData || canEditProject}
                isDisabled={!!initialData && !canEditProject}
                isLoading={assignedProjects === null}
                placeholder={assignedProjects === null ? "Henter konti..." : "Vælg en konto..."}
                noOptionsMessage={() => "Ingen konti fundet"}
                styles={{
                  control: (provided) => ({ ...provided, minHeight: '44px', padding: '2px 4px', fontSize: '16px' }),
                  placeholder: (provided) => ({ ...provided, fontSize: '16px' }),
                  input: (provided) => ({ ...provided, fontSize: '16px' }),
                  singleValue: (provided) => ({ ...provided, fontSize: '16px' }),
                }}
              />
            ) : (
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadAccountOptions}
                value={accountId ? { value: accountId, label: accountName } : null}
                onChange={(selectedOption) => {
                  if (!initialData || canEditProject) {
                    setAccountId(selectedOption?.value || '');
                    setAccountName(selectedOption?.label || '');
                    setProjectId('');
                    setNonBemandingTaskId('');
                  }
                }}
                isClearable={!initialData || canEditProject}
                isDisabled={!!initialData && !canEditProject}
                placeholder="Søg efter en konto..."
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? "Ingen konti fundet" : "Ingen konti tilgængelige"
                }
                styles={{
                  control: (provided) => ({ ...provided, minHeight: '44px', padding: '2px 4px', fontSize: '16px' }),
                  placeholder: (provided) => ({ ...provided, fontSize: '16px' }),
                  input: (provided) => ({ ...provided, fontSize: '16px' }),
                  singleValue: (provided) => ({ ...provided, fontSize: '16px' }),
                }}
              />
            )}
            {hasSubmitted && errors.accountId && <div className="text-red-500 text-sm mt-1">{errors.accountId}</div>}
          </div>

          {/* Projekt (optional, depends on Kunde) */}
          <div className="mb-2 flex flex-col">
            <label className="text-sm md:text-base font-medium mb-1">Projekt:</label>
            <Select
              options={projectOptions}
              value={projectOptions.find(option => option.value === projectId) || null}
              onChange={(selectedOption) => {
                if (!initialData || canEditProject) {
                  setProjectId(selectedOption?.value || '');
                  setNonBemandingTaskId('');
                }
              }}
              isClearable
              isDisabled={!accountId || isLoadingAccountProjects || (!!initialData && !canEditProject)}
              isLoading={isLoadingAccountProjects}
              placeholder={
                !accountId ? "Vælg først en konto..." :
                isLoadingAccountProjects ? "Henter projekter..." :
                "Vælg et projekt (valgfrit)..."
              }
              styles={{
                control: (provided) => ({
                  ...provided,
                  minHeight: '44px',
                  padding: '2px 4px',
                  fontSize: '16px',
                }),
                placeholder: (provided) => ({
                  ...provided,
                  fontSize: '16px',
                }),
                input: (provided) => ({
                  ...provided,
                  fontSize: '16px',
                }),
                singleValue: (provided) => ({
                  ...provided,
                  fontSize: '16px',
                }),
              }}
            />
            {hasSubmitted && errors.projectId && <div className="text-red-500 text-sm mt-1">{errors.projectId}</div>}
          </div>

          {/* Tasks dropdown — always visible */}
          <div className="mb-2 flex flex-col">
            <label className="text-sm md:text-base font-medium mb-1">Opgave:</label>
            <Select
              options={nonBemandingTasks.map(t => ({ value: t.id, label: t.name }))}
              value={nonBemandingTaskId
                ? (nonBemandingTasks.map(t => ({ value: t.id, label: t.name })).find(o => o.value === nonBemandingTaskId)
                  || (initialData?.taskName ? { value: nonBemandingTaskId, label: initialData.taskName } : null))
                : null}
              onChange={(opt) => setNonBemandingTaskId(opt?.value || '')}
              isClearable
              isDisabled={readonly || !projectId}
              placeholder={!projectId ? "Vælg projekt først..." : "Vælg opgave..."}
              styles={{
                control: (provided) => ({ ...provided, minHeight: '44px', padding: '2px 4px', fontSize: '16px' }),
                placeholder: (provided) => ({ ...provided, fontSize: '16px' }),
                input: (provided) => ({ ...provided, fontSize: '16px' }),
                singleValue: (provided) => ({ ...provided, fontSize: '16px' }),
              }}
            />
            {hasSubmitted && errors.nonBemandingTaskId && <div className="text-red-500 text-sm mt-1">{errors.nonBemandingTaskId}</div>}
          </div>
          </>}

          {/* Notes som fuld bredde */}
          <div className="mb-2 flex flex-col">
            <label className="text-sm md:text-base font-medium mb-1">Bemærkning:</label>
            <textarea
              value={title}
              onChange={(e) => {
                console.log('Notes changed, isCRMSync:', isCRMSync, 'readonly:', readonly);
                const newValue = e.target.value.trimStart();
                const selected = projectTimecodes.find(tc => tc.id === timecodeId);

                // Check if this is a Non-Billable worktype and user is typing after worktype abbreviation
                if (selected && (selected.billingtype === 'Non Billable' || billableAutoFill) && selected.name) {
                  const workTypeName = selected.name;
                  const abbreviation = extractAbbreviation(workTypeName);

                  // If current value is just the worktype abbreviation and user is adding text
                  if (title === abbreviation && newValue.length > abbreviation.length && !newValue.includes(' | ')) {
                    // Add " | " separator and the new text
                    const userText = newValue.substring(abbreviation.length);
                    setTitle(`${abbreviation} | ${userText}`);
                    return;
                  }
                }

                setTitle(newValue);
              }}
              className={`p-2 text-base rounded border border-gray-300 resize-none touch-manipulation ${(readonly && !isCRMSync) ? 'bg-gray-50 text-gray-400' : ''}`}
              placeholder="Skriv bemærkning..."
              disabled={readonly && !isCRMSync}
              maxLength={255}
              rows={4}
              style={{ fontSize: '16px' }}
            />
            <div className="text-sm text-gray-600 text-right">
              {title.length}/255
            </div>
            {hasSubmitted && errors.title && <div className="text-red-500 text-sm mt-1">{errors.title}</div>}
          </div>

          {/* Timer — only shown for new entries */}
          {!initialData && (() => {
            const canStartTimer = !!(accountId && projectId && nonBemandingTaskId);
            const startDisabled = !timerStartedAt && !canStartTimer;
            return (
              <div className="mt-4 flex flex-col items-center gap-2">
                <button
                  onClick={timerStartedAt ? handleStopTimer : handleStartTimer}
                  disabled={startDisabled}
                  title={startDisabled ? 'Vælg Kunde, Projekt og Opgave før du starter timeren' : undefined}
                  className={`w-full py-3 rounded font-medium text-white transition-colors ${
                    timerStartedAt ? 'bg-red-700 hover:bg-red-800' :
                    startDisabled ? 'bg-gray-300 cursor-not-allowed' :
                    'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {timerStartedAt ? 'Stop' : 'Start'}
                </button>
                <TimerDisplay startedAt={timerStartedAt} clockOffset={timerClockOffset} />
              </div>
            );
          })()}

          {/* Fakturerbar */}
          {!readonly && (
            <div className="mt-4 flex items-center gap-2">
              <input
                type="checkbox"
                id="fakturerbar"
                checked={billingtype !== 'Non Billable'}
                onChange={e => setBillingType(e.target.checked ? 'Billable' : 'Non Billable')}
                className="w-4 h-4 accent-[#537D5D]"
              />
              <label htmlFor="fakturerbar" className="text-sm text-gray-700 select-none cursor-pointer">Fakturerbar</label>
            </div>
          )}

          </>} {/* End formType === 'projects' */}

          </div> {/* End Scrollable Content */}

          {/* Sticky Action Buttons */}
          <div className="border-t border-gray-200 p-3 md:p-6 bg-white rounded-b-lg">
          <div className="flex flex-col md:flex-row justify-between gap-3 md:gap-0">
            {initialData?.isPeopleLeave ? (
              isAdmin && (
                <div />
              )
            ) : !readonly && (
              <>
                <div className="order-2 md:order-1">
                  {initialData && (formType !== 'workday' || initialData.isPeople) && (
                    <button
                      onClick={handleDelete}
                      className="w-full md:w-auto px-6 py-3 md:px-4 md:py-2 bg-red-600 text-white rounded hover:bg-red-700 touch-manipulation font-medium"
                    >
                      Slet
                    </button>
                  )}
                </div>
                <div className="order-1 md:order-2 flex flex-col items-end gap-1">
                  <button
                    onClick={formType === 'people' ? handleLeaveSubmit : formType === 'workday' ? handleSubmitWorkday : handleSubmit}
                    className="w-full md:w-auto px-6 py-3 md:px-4 md:py-2 bg-[#537D5D] text-white rounded hover:bg-[#73946B] disabled:opacity-50 touch-manipulation font-medium"
                    disabled={
                      readonly ||
                      (formType === 'workday' && (isSubmittingWorkday || (!initialData && !!workdayTimerStartedAt))) ||
                      (formType === 'people' && isSubmittingLeave) ||
                      (formType === 'projects' && !initialData && !!timerStartedAt)
                    }
                  >
                    {formType === 'workday' ? (isSubmittingWorkday ? 'Sender...' : 'Gem') : formType === 'people' ? (isSubmittingLeave ? 'Sender...' : 'Anmod om fravær') : 'Gem'}
                  </button>
                  {!initialData && ((formType === 'projects' && timerStartedAt) || (formType === 'workday' && workdayTimerStartedAt)) && (
                    <p className="text-xs text-red-500 whitespace-nowrap">Stop timer før du gemmer</p>
                  )}
                </div>
              </>
            )}
          </div>
          </div> {/* End Sticky Action Buttons */}
        </div>
      
      <ConfirmModal 
        isOpen={showConfirmModal}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        title="Ugemte ændringer"
        message={
          timerStoppedInTab === 'projects' ? 'Du har ugemte ændringer i din projekt tidslog. Vil du forkaste dem?' :
          timerStoppedInTab === 'workday' ? 'Du har ugemte ændringer i din arbejdstid tidslog. Vil du forkaste dem?' :
          'Er du sikker på at du vil forkaste dine ændringer?'
        }
        confirmText="Forkast"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Slet registrering"
        message="Er du sikker på at du vil slette denne tidsregistrering?"
        confirmText="Slet"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
      <ConfirmModal
        isOpen={showLeaveConfirm}
        onConfirm={handleLeaveConfirmed}
        onCancel={() => setShowLeaveConfirm(false)}
        title="Bekræft fraværsanmodning"
        message={`Send anmodning om fravær fra ${leaveStartDate} til ${leaveEndDate}?`}
        confirmText="Send"
        confirmButtonClass="bg-[#537D5D] hover:bg-[#73946B]"
      />
      <ConfirmModal
        isOpen={showMarkDoneModal}
        onConfirm={handleMarkDoneConfirm}
        onCancel={handleMarkDoneCancel}
        title="Vil du markere opgaven som udført?"
        message=""
        confirmText="Ja"
        confirmButtonClass="bg-green-600 hover:bg-green-700"
        cancelText="Nej"
        cancelButtonClass="text-white bg-red-600 hover:bg-red-700"
      />
      {showTimerReminder && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-4 bg-yellow-50 border border-yellow-300 text-yellow-800 rounded-lg px-6 py-4 shadow-lg text-base font-medium whitespace-nowrap">
          <span>Husk at gemme tidsregistreringen!</span>
          <button onClick={() => setShowTimerReminder(false)} className="text-yellow-600 hover:text-yellow-900 font-bold">✕</button>
        </div>
      )}
    </>
  );
}